import { Calendar, Card, Select, Space, Tag } from 'antd';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadSingleLeaveHistory } from '../../redux/rtk/features/leave/leaveSlice';
import { loadAllPublicHoliday } from '../../redux/rtk/features/publicHoliday/publicHolidaySlice';
import { loadAllWeeklyHoliday } from '../../redux/rtk/features/weeklyHoliday/weeklyHolidaySlice';
import getUserFromToken from '../../utils/getUserFromToken';
import getWeeklyOff from '../../utils/getWeeklyOff';

// Add the isBetween plugin to dayjs
dayjs.extend(isBetween);

const AttendanceCalendar = ({ id }) => {
    const dispatch = useDispatch();
    const userId = getUserFromToken();
    const [monthData, setMonthData] = useState([]);
    const [selectedDate, setSelectedDate] = useState(dayjs());

    // Get data from Redux
    const weeklyHoliday = useSelector((state) => state.weeklyHoliday.list || []);
    const publicHolidayData = useSelector((state) => state.publicHoliday.list || []);
    const leaveHistoryData = useSelector((state) => state.leaveHistory || []);
    const attendance = useSelector((state) => state.attendance?.list || []);

    const publicHolidays = Array.isArray(publicHolidayData) ? publicHolidayData : [];
    const leaveHistory = Array.isArray(leaveHistoryData) ? leaveHistoryData : [];

    // Initial data loading
    useEffect(() => {
        const loadData = async () => {
            try {
                await Promise.all([
                    dispatch(loadAllPublicHoliday()),
                    dispatch(loadAllWeeklyHoliday()),
                    dispatch(loadSingleLeaveHistory(userId))
                ]);
            } catch (error) {
                console.error('Error loading calendar data:', error);
            }
        };
        loadData();
    }, [dispatch, userId]);

    // Calculate weekly off days for selected month
    const currentMonthWeeklyOffDays = useMemo(() => {
        if (!weeklyHoliday || !userId) return new Map();
        
        const startOfMonth = selectedDate.startOf('month');
        const endOfMonth = selectedDate.endOf('month');
        const offDaysMap = new Map();
        
        let currentDate = startOfMonth;
        while (currentDate.isBefore(endOfMonth) || currentDate.isSame(endOfMonth, 'day')) {
            const dateStr = currentDate.format('YYYY-MM-DD');
            offDaysMap.set(dateStr, getWeeklyOff(dateStr, weeklyHoliday, userId));
            currentDate = currentDate.add(1, 'day');
        }
        
        return offDaysMap;
    }, [selectedDate, weeklyHoliday, userId]);

    // Calculate dates for the selected month
    const getCurrentMonthDates = useCallback(() => {
        const startOfMonth = selectedDate.startOf('month');
        const endOfMonth = selectedDate.endOf('month');
        const dates = [];

        let currentDate = startOfMonth;
        while (currentDate.isBefore(endOfMonth) || currentDate.isSame(endOfMonth, 'day')) {
            const date = currentDate.format('YYYY-MM-DD');
            const isFutureDate = currentDate.isAfter(dayjs(), 'day');
            const weeklyOff = currentMonthWeeklyOffDays.get(date);

            const publicHol = publicHolidays.some(holiday => 
                dayjs(holiday.date).format('YYYY-MM-DD') === date
            );

            const leave = leaveHistory.some(leave =>
                dayjs(date).isBetween(
                    dayjs(leave.leaveFrom).startOf('day'),
                    dayjs(leave.leaveTo).endOf('day'),
                    null,
                    '[]'
                )
            );

            const attendanceData = attendance.find(a => 
                dayjs(a.inTime).format('YYYY-MM-DD') === date
            );

            let status = 'Absent';
            let statusDetails = null;

            if (!weeklyOff) {
                status = 'Weekly Off';
            } else if (publicHol) {
                status = 'Public Holiday';
                statusDetails = publicHolidays.find(h => 
                    dayjs(h.date).format('YYYY-MM-DD') === date
                )?.name;
            } else if (leave) {
                status = 'Leave';
            } else if (attendanceData) {
                status = 'Present';
            } else if (isFutureDate) {
                status = "-";
            }

            dates.push({
                date: currentDate.toDate(),
                status,
                statusDetails,
                inTime: attendanceData?.inTime,
                outTime: attendanceData?.outTime,
                totalHours: attendanceData?.totalHour,
                inTimeStatus: attendanceData?.inTimeStatus,
                outTimeStatus: attendanceData?.outTimeStatus,
            });

            currentDate = currentDate.add(1, 'day');
        }
        return dates;
    }, [selectedDate, currentMonthWeeklyOffDays, publicHolidays, leaveHistory, attendance]);

    // Update month data when dependencies change
    useEffect(() => {
        if (weeklyHoliday?.length && publicHolidays?.length) {
            const dates = getCurrentMonthDates();
            setMonthData(dates);
        }
    }, [getCurrentMonthDates, weeklyHoliday, publicHolidays]);

    // Handlers for month and year selection
    const handleMonthChange = useCallback((value) => {
        setSelectedDate(prev => prev.month(value));
    }, []);

    const handleYearChange = useCallback((value) => {
        setSelectedDate(prev => prev.year(value));
    }, []);

    // Memoized options for dropdowns
    const monthOptions = useMemo(() => 
        Array.from({ length: 12 }, (_, i) => ({
            value: i,
            label: dayjs().month(i).format('MMMM'),
        })),
        []
    );

    const yearOptions = useMemo(() => 
        Array.from({ length: 5 }, (_, i) => ({
            value: dayjs().year() - i,
            label: `${dayjs().year() - i}`,
        })),
        []
    );

    // Memoized cell renderer
    const dateCellRender = useCallback((date) => {
        const statusData = monthData.find(item => dayjs(item.date).isSame(date, 'day'));
        if (!statusData || !statusData.status) return null;

        const { status, statusDetails, inTime, outTime } = statusData;

        return (
            <div style={{ padding: '5px', textAlign: 'center' }}>
                <Tag color={statusColors[status]} style={{ width: '100%', textAlign: 'center' }}>
                    {status}
                </Tag>
                {status === 'Present' && (
                    <div style={{ fontSize: '12px', marginTop: '5px' }}>
                        (In: {dayjs(inTime).utc().format('hh:mm A')}, 
                         Out: {outTime ? dayjs(outTime).utc().format('hh:mm A') : 'N/A'})
                    </div>
                )}
                {statusDetails && <div style={{ fontSize: '12px' }}>{statusDetails}</div>}
            </div>
        );
    }, [monthData]);

        const statusColors = {
        Present: 'green',
        Absent: 'red',
        'Weekly Off': 'grey',
        'Public Holiday': 'blue',
        Leave: 'orange'
    };

    // Render calendar
    return (
        <Card className="w-full">
            <Space style={{ marginBottom: 16 }}>
                <Select
                    value={selectedDate.month()}
                    onChange={handleMonthChange}
                    options={monthOptions}
                    style={{ width: 120 }}
                />
                <Select
                    value={selectedDate.year()}
                    onChange={handleYearChange}
                    options={yearOptions}
                    style={{ width: 120 }}
                />
            </Space>
            <Calendar
                value={selectedDate}
                dateCellRender={dateCellRender}
                fullscreen={false}
                headerRender={({ value, type, onChange }) => null}
                onChange={(newDate) => setSelectedDate(newDate)}
            />
        </Card>
    );
};

export default AttendanceCalendar;